/** What the blocked mail actually was (L1): a three-segment donut.
 *
 *  Part-to-whole with few segments is a donut with the total in the hole
 *  (dashboard design rules §2). Three segments by construction — the pinned
 *  definition has exactly three classifications — so this can never breach the
 *  ≤5-segment rule, and there is no "Other" bucket to hide behind.
 *
 *  Spam is deliberately not a segment. Including it would inflate the headline
 *  with commercial noise and make the block rate meaningless, which is the whole
 *  reason the definition excludes it. The caption says so, because a missing
 *  segment that nobody explains reads as a bug.
 */

import { Chart, type ChartConfig } from "@norican/dataviz";
import { type DataGridColumnDef, EmptyState, Typography } from "@norican/react";

import { ChartCard, type Verdict } from "@/components/board";
import type { ClassificationSlice } from "@/lib/api";

import { MIX_ANCHOR } from "./exec-tiles";
import { CLASSIFICATION_COLOR, EXEC_STALE_AFTER_MS, formatCount, formatPct } from "./status";

/** `?data=classification-mix` opens this chart's table twin. */
const TWIN_ID = "classification-mix";

interface ClassificationDonutProps {
  slices: ClassificationSlice[];
  blockedTotal: number;
  windowDays: number;
  generatedAt: string | null | undefined;
}

export function ClassificationDonut({
  slices,
  blockedTotal,
  windowDays,
  generatedAt,
}: ClassificationDonutProps) {
  // Fixed colour assignment from the full slice list, so a zero-count class
  // keeps its colour rather than letting the others shift (§3.3).
  const config: ChartConfig = Object.fromEntries(
    slices.map((slice) => [
      slice.label,
      { label: slice.label, color: CLASSIFICATION_COLOR[slice.classification] },
    ]),
  );
  const segments = slices.filter((slice) => slice.blocked > 0);

  const twinColumns: DataGridColumnDef<ClassificationSlice>[] = [
    { id: "label", header: "Classification", accessor: (row) => row.label },
    {
      id: "blocked",
      header: "Blocked",
      numeric: true,
      accessor: (row) => row.blocked,
      cell: (row) => <span className="tabular-nums">{formatCount(row.blocked)}</span>,
    },
    {
      id: "share",
      header: "Share",
      numeric: true,
      accessor: (row) => row.share_pct,
      cell: (row) => <span className="tabular-nums">{formatPct(row.share_pct)}</span>,
    },
  ];

  return (
    <section id={MIX_ANCHOR} className="scroll-mt-6">
      <ChartCard
        title="What was blocked"
        caption={`Last ${windowDays} days · spam excluded by definition`}
        since={generatedAt}
        staleAfterMs={EXEC_STALE_AFTER_MS}
        verdict={mixVerdict(segments)}
        tableTwin={{
          id: TWIN_ID,
          ariaLabel: "Blocked dangerous email by classification",
          data: slices,
          columns: twinColumns,
          getRowId: (row) => row.classification,
        }}
      >
        {segments.length === 0 ? (
          <EmptyState
            size="sm"
            title="Nothing blocked in this window"
            description="No malware, phish or impostor message has been recorded yet."
          />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="relative">
              <Chart.Container
                config={config}
                aria-label={`Composition of ${formatCount(blockedTotal)} blocked dangerous messages by classification: malware, impostor and phish`}
                className="h-60"
              >
                <Chart.PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                  <Chart.Tooltip
                    content={
                      <Chart.TooltipContent
                        hideLabel
                        formatter={(value) => `${formatCount(Number(value))} messages`}
                      />
                    }
                  />
                  <Chart.Pie
                    {...Chart.seriesDefaults}
                    data={segments}
                    dataKey="blocked"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="82%"
                    paddingAngle={2}
                    cornerRadius={3}
                    stroke="var(--norican-surface)"
                    strokeWidth={2}
                  >
                    {segments.map((slice) => (
                      <Chart.Cell
                        key={slice.classification}
                        fill={CLASSIFICATION_COLOR[slice.classification]}
                      />
                    ))}
                  </Chart.Pie>
                </Chart.PieChart>
              </Chart.Container>
              {/* Centre total, per the kit's Donut story: overlaid, never a slice. */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
              >
                <Typography variant="caption" className="font-semibold tracking-wide uppercase">
                  Blocked
                </Typography>
                <Typography variant="heading-2" as="span">
                  {formatCount(blockedTotal)}
                </Typography>
              </div>
            </div>
            {/* Legend below the donut so it does not push the centred total
                off-centre. Segments are never colour-alone — each is labelled. */}
            <div className="text-fg-muted flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs">
              {slices.map((slice) => (
                <span key={slice.classification} className="inline-flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className="size-2.5 rounded-full"
                    style={{ background: CLASSIFICATION_COLOR[slice.classification] }}
                  />
                  {slice.label} · <span className="tabular-nums">{formatCount(slice.blocked)}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </ChartCard>
    </section>
  );
}

/** Which vector leads, said as a sentence rather than left in the legend. */
function mixVerdict(segments: ClassificationSlice[]): Verdict {
  if (segments.length === 0) {
    return {
      label: "No data",
      tone: "neutral",
      text: "Nothing dangerous recorded in this window, so there is no mix to read.",
    };
  }
  const leader = segments.reduce((worst, slice) => (slice.blocked > worst.blocked ? slice : worst));
  return {
    label: "Noted",
    tone: "neutral",
    text: `${leader.label} leads at ${formatPct(leader.share_pct)} of everything blocked — that is where the awareness training has to bite.`,
  };
}
